<script setup lang="ts">
import type { PositionColumn, PositionRow, PredefinedPosition } from '@/components/finest-custom-position-list/types'
import FinestCustomPositionListCard from '@/components/FinestCustomPositionListCard.vue'
import { idOf, usePersistedRows } from '@/composables/usePersistedRows'
import { assetGroups } from '@/pages/investors/credit/categories'
import { assetPayload, blankAssetRow, isAssetDraft } from '@/pages/investors/credit/creditPositions'
import type { AssetGroupKey } from '@/pages/investors/credit/types'
import { requestJson } from '@/utils/postJson'
import AssetPositionController from '@controllers/Investors/AssetPositionController'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AssetPositionData = App.Data.SelfDisclosure.AssetPositionData

const props = defineProps<{
  group: AssetGroupKey
  personId: string
  serverRows: PositionRow[]
}>()

const { t } = useI18n({ useScope: 'local' })

const positionRoute = (id: string) => ({ naturalPerson: props.personId, assetPosition: id })

const { rows, reset, addDraft, removeRow, persistOnLeave } = usePersistedRows({
  toPayload: (row: PositionRow) => assetPayload(row, props.group),
  isDraft: isAssetDraft,
  create: async (payload) =>
    idOf(await requestJson<AssetPositionData>('post', AssetPositionController.store(props.personId).url, payload)),
  update: (id, payload) => requestJson('put', AssetPositionController.update(positionRoute(id)).url, payload),
  destroy: (id) => requestJson('delete', AssetPositionController.destroy(positionRoute(id)).url, undefined),
  reloadProps: ['people', 'creditCompletion'],
})

reset(props.serverRows)

watch(() => props.serverRows, reset)

const types = computed<PredefinedPosition[]>(() =>
  assetGroups[props.group].map((key) => ({ key, label: t(`types.${key}`) })),
)

const labelOf = (row: PositionRow): string =>
  row.predefinedKey
    ? (types.value.find((type) => type.key === row.predefinedKey)?.label ?? row.predefinedKey)
    : String(row.cells.label ?? '')

const valueColumns = computed<PositionColumn[]>(() =>
  props.group === 'investment'
    ? [
        { key: 'amount', label: t('columns.amount'), type: 'currency', fractionDigits: 0, total: true },
        { key: 'institute', label: t('columns.institute'), type: 'text' },
      ]
    : [
        { key: 'units', label: t('columns.units'), type: 'number', fractionDigits: 0, total: true },
        { key: 'market_value', label: t('columns.marketValue'), type: 'currency', fractionDigits: 0, total: true },
      ],
)

const columns = computed<PositionColumn[]>(() => [
  { key: 'label', label: t(`labels.${props.group}`), type: 'suggest', suggestions: types.value },
  ...valueColumns.value,
])

const labelledRows = computed({
  get: () => rows.value.map((row) => ({ ...row, cells: { ...row.cells, label: labelOf(row) } })),
  set: (next: PositionRow[]) => (rows.value = next),
})

const accents: Record<AssetGroupKey, string> = {
  investment: 'bg-brand-blue-2',
  real_estate: 'bg-brand-teal',
}
</script>

<template>
  <section class="bg-surface-2! fh-card shadow-none!" @focusout="persistOnLeave">
    <header class="flex items-start gap-3 px-6 py-4">
      <span class="mt-1 h-8 w-1 flex-none rounded-full" :class="accents[group]" />
      <div>
        <h3 class="text-base font-bold">{{ t(`groups.${group}`) }}</h3>
        <p class="text-ink-3 text-sm">{{ t(`hints.${group}`) }}</p>
      </div>
    </header>

    <div class="py-5">
      <FinestCustomPositionListCard
        v-model:rows="labelledRows"
        plain
        flush
        title=""
        :columns="columns"
        editable-rows
        @add-row="addDraft(blankAssetRow())"
        @remove-row="removeRow" />
    </div>
  </section>
</template>

<i18n lang="json">
{
  "de": {
    "groups": {
      "investment": "Anlagevermögen",
      "real_estate": "Immobilien"
    },
    "hints": {
      "investment": "Angelegtes Kapital mit dem Institut, bei dem es liegt.",
      "real_estate": "Immobilien im Eigentum, je Art mit Anzahl der Einheiten und Marktwert."
    },
    "labels": {
      "investment": "Anlageart",
      "real_estate": "Immobilienart"
    },
    "columns": {
      "amount": "Betrag",
      "institute": "Institut",
      "units": "Anzahl Einheiten",
      "marketValue": "Marktwert"
    },
    "types": {
      "bank_balance": "Bank- und Sparguthaben",
      "building_saving_balance": "Bausparguthaben",
      "securities": "Wertpapier",
      "life_insurance": "Lebensversicherungen",
      "pension_insurance": "Rentenversicherungen",
      "other_investment": "Sonstige",
      "condominium": "Eigentumswohnung",
      "single_family_house": "Einfamilienhaus",
      "apartment_building": "Mehrfamilienhaus",
      "two_party_house": "Zweifamilienhaus",
      "office": "Büro",
      "commercial": "Gewerbe",
      "other_real_estate": "Sonstige"
    }
  },
  "en": {
    "groups": {
      "investment": "Invested assets",
      "real_estate": "Real estate"
    },
    "hints": {
      "investment": "Invested capital together with the institute holding it.",
      "real_estate": "Real estate you own, per type with the number of units and its market value."
    },
    "labels": {
      "investment": "Asset type",
      "real_estate": "Property type"
    },
    "columns": {
      "amount": "Amount",
      "institute": "Institute",
      "units": "Number of units",
      "marketValue": "Market value"
    },
    "types": {
      "bank_balance": "Bank and savings balance",
      "building_saving_balance": "Building savings balance",
      "securities": "Securities",
      "life_insurance": "Life insurances",
      "pension_insurance": "Pension insurances",
      "other_investment": "Other",
      "condominium": "Condominium",
      "single_family_house": "Single-family house",
      "apartment_building": "Apartment building",
      "two_party_house": "Two-party house",
      "office": "Office",
      "commercial": "Commercial",
      "other_real_estate": "Other"
    }
  }
}
</i18n>
